import React, { useEffect, useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import CreateBookingModal from '../../../components/CreateBookingModal.tsx';
import CustomerModal from '../../../components/CustomerModal.tsx';
import ConfirmDialog from '../../../components/ConfirmDialog.tsx';
import AddNoteModal from '../../../components/AddNoteModal.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faChevronLeft,
  faChevronRight,
  faEdit,
  faEnvelope,
  faFilter,
  faPhone,
  faSearch,
  faStar,
  faStickyNote,
  faTrash,
  faUserPlus,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { useCustomerStore } from '../../../features/customers/store/useCustomerStore';
import { useParams } from '@router.ts';
import { useStaffLoV } from '../../../features/services/hooks/useStaffLoV';
import { useServicesLoV } from '../../../features/services/hooks/useServicesLoV';
import { useToast } from '../../../hooks/useToast';
import type { AppCreateCustomerRequest, AppUpdateCustomerRequest, CustomerDto } from '../../../api/generated';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';

const CustomersPage: React.FC = () => {
  const navigate = useNavigate();
  const { entityId } = useParams('/app/:entityId');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('firstName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedCustomerForBooking, setSelectedCustomerForBooking] = useState<CustomerDto | null>(null);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [customerModalMode, setCustomerModalMode] = useState<'create' | 'edit'>('create');
  const [editingCustomer, setEditingCustomer] = useState<CustomerDto | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<CustomerDto | null>(null);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [customerForNote, setCustomerForNote] = useState<CustomerDto | null>(null);

  const breadcrumb = [{ label: 'Zákazníci' }, { label: 'Seznam zákazníků' }];

  // Store
  const { customers, pagination, loading, error, loadCustomers, createCustomer, updateCustomer, deleteCustomer, createCustomerNote, uploadNoteAttachment } =
    useCustomerStore();

  // Načtení dat pro modal rezervace
  const { staff: staffList } = useStaffLoV(true);
  const { services: servicesList } = useServicesLoV(true);

  const toast = useToast();

  // Load customers on component mount and when filters change
  useEffect(() => {
    const loadData = async () => {
      await loadCustomers({
        query: searchTerm || undefined,
        page: currentPage,
        size: 20,
        sortBy,
        sortOrder,
      });
    };
    loadData();
  }, [searchTerm, currentPage, sortBy, sortOrder, loadCustomers]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== '') {
        setCurrentPage(1); // Reset to first page when searching
        loadCustomers({
          query: searchTerm,
          page: 1,
          size: 20,
          sortBy,
          sortOrder,
        });
      } else {
        loadCustomers({
          page: currentPage,
          size: 20,
          sortBy,
          sortOrder,
        });
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, loadCustomers, sortBy, sortOrder]);

  // Filter customers by status (API doesn't support status filtering yet)
  const filteredCustomers = customers.filter(customer => {
    const matchesFilter = filterStatus === 'all' || (filterStatus === 'active' && customer.isActive) || (filterStatus === 'inactive' && !customer.isActive);
    return matchesFilter;
  });

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const getStatusText = (isActive: boolean) => {
    return isActive ? 'Aktivní' : 'Neaktivní';
  };

  // Calculate statistics
  const totalCustomers = pagination?.totalElements || customers.length;
  const activeCustomers = customers.filter(c => c.isActive).length;
  const inactiveCustomers = customers.filter(c => !c.isActive).length;

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleOpenBookingModal = (customer: CustomerDto, e: React.MouseEvent) => {
    e.stopPropagation(); // Zabrání prokliknutí na detail
    setSelectedCustomerForBooking(customer);
    setIsBookingModalOpen(true);
  };

  const handleCloseBookingModal = () => {
    setIsBookingModalOpen(false);
    setSelectedCustomerForBooking(null);
  };

  const handleRowClick = (customerId: string) => {
    navigate(`/app/${entityId}/customer-detail/${customerId}`);
  };

  const handleOpenCreateCustomerModal = () => {
    setCustomerModalMode('create');
    setEditingCustomer(null);
    setIsCustomerModalOpen(true);
  };

  const handleOpenEditCustomerModal = (customer: CustomerDto, e: React.MouseEvent) => {
    e.stopPropagation();
    setCustomerModalMode('edit');
    setEditingCustomer(customer);
    setIsCustomerModalOpen(true);
  };

  const handleCloseCustomerModal = () => {
    setIsCustomerModalOpen(false);
    setEditingCustomer(null);
  };

  const handleSaveCustomer = async (data: AppCreateCustomerRequest | AppUpdateCustomerRequest, customerId?: string) => {
    try {
      if (customerModalMode === 'create') {
        await createCustomer(data as AppCreateCustomerRequest);
      } else if (customerModalMode === 'edit' && customerId) {
        await updateCustomer(customerId, data as AppUpdateCustomerRequest);
      }

      // Reload customers
      await loadCustomers({
        query: searchTerm || undefined,
        page: currentPage,
        size: 20,
        sortBy,
        sortOrder,
      });

      handleCloseCustomerModal();
    } catch (error) {
      console.error('Chyba při ukládání zákazníka:', error);
      throw error;
    }
  };

  const handleOpenDeleteDialog = (customer: CustomerDto, e: React.MouseEvent) => {
    e.stopPropagation();
    setCustomerToDelete(customer);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setCustomerToDelete(null);
  };

  const handleDeleteCustomer = async () => {
    if (!customerToDelete) return;

    try {
      await deleteCustomer(customerToDelete.id);

      // Reload customers
      await loadCustomers({
        query: searchTerm || undefined,
        page: currentPage,
        size: 20,
        sortBy,
        sortOrder,
      });

      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Chyba při mazání zákazníka:', error);
      throw error;
    }
  };

  const handleOpenAddNoteModal = (customer: CustomerDto, e: React.MouseEvent) => {
    e.stopPropagation();
    setCustomerForNote(customer);
    setIsAddNoteModalOpen(true);
  };

  const handleSaveNote = async (noteText: string, images: File[]) => {
    if (!customerForNote) return;

    try {
      // Vytvoř poznámku
      const createdNote = await createCustomerNote(customerForNote.id, {
        noteText,
      });

      // Nahraj obrázky pokud jsou
      if (images.length > 0) {
        for (const image of images) {
          await uploadNoteAttachment(customerForNote.id, createdNote.id, image);
        }
      }

      toast.success('Úspěch!', `Poznámka k zákazníkovi ${customerForNote.firstName} ${customerForNote.lastName} byla přidána`);

      setIsAddNoteModalOpen(false);
      setCustomerForNote(null);
    } catch (error) {
      console.error('Chyba při přidávání poznámky:', error);
      toast.error('Chyba!', 'Nepodařilo se přidat poznámku');
      throw error;
    }
  };

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Seznam zákazníků" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Zákazníci</h1>
                <p className="text-gray-600 mt-1">Spravujte databázi zákazníků</p>
              </div>
              <div className="flex items-center space-x-3">
                <button onClick={handleOpenCreateCustomerModal} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                  <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
                  Nový zákazník
                </button>
              </div>
            </div>
          </section>

          {/* Stats Cards */}
          <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <FontAwesomeIcon icon={faUsers} className="text-xl text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkem zákazníků</p>
                  <p className="text-2xl font-bold text-gray-900">{totalCustomers}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-50 rounded-lg">
                  <FontAwesomeIcon icon={faUsers} className="text-xl text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Aktivní</p>
                  <p className="text-2xl font-bold text-gray-900">{activeCustomers}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-red-50 rounded-lg">
                  <FontAwesomeIcon icon={faUsers} className="text-xl text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Neaktivní</p>
                  <p className="text-2xl font-bold text-gray-900">{inactiveCustomers}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <FontAwesomeIcon icon={faStar} className="text-xl text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Průměrné hodnocení</p>
                  <p className="text-2xl font-bold text-gray-900">4.4</p>
                </div>
              </div>
            </div>
          </section>

          {/* Search and Filters */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Hledat zákazníky..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                />
              </div>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
              >
                <option value="all">Všichni zákazníci</option>
                <option value="active">Aktivní</option>
                <option value="inactive">Neaktivní</option>
              </select>
              <button className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <FontAwesomeIcon icon={faFilter} className="w-4 h-4 mr-2" />
                Více filtrů
              </button>
            </div>
          </section>

          {/* Customers Table */}
          <section className="bg-white rounded-lg shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('firstName')}
                    >
                      <div className="flex items-center">
                        Zákazník
                        {sortBy === 'firstName' && <span className="ml-1 text-green-600">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kontakt</th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center">
                        Vytvořen
                        {sortBy === 'createdAt' && <span className="ml-1 text-green-600">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statistiky</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hodnocení</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Akce</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mr-2"></div>
                          Načítání zákazníků...
                        </div>
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-red-500">
                        <div className="flex items-center justify-center">
                          <FontAwesomeIcon icon={faUsers} className="w-5 h-5 mr-2" />
                          {error}
                        </div>
                      </td>
                    </tr>
                  ) : filteredCustomers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                        <div className="flex items-center justify-center">
                          <FontAwesomeIcon icon={faUsers} className="w-5 h-5 mr-2" />
                          Žádní zákazníci nenalezeni
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredCustomers.map(customer => (
                      <tr key={customer.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRowClick(customer.id)}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                              <FontAwesomeIcon icon={faUsers} className="w-5 h-5 text-green-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {customer.firstName} {customer.lastName}
                              </div>
                              <div className="text-sm text-gray-500">
                                <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(customer.isActive)}`}>{getStatusText(customer.isActive)}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <FontAwesomeIcon icon={faPhone} className="w-4 h-4 mr-2 text-gray-400" />
                            {customer.phone}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center">
                            <FontAwesomeIcon icon={faEnvelope} className="w-4 h-4 mr-2 text-gray-400" />
                            {customer.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {customer.createdAt ? new Date(customer.createdAt).toLocaleDateString('cs-CZ') : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">-</div>
                          <div className="text-sm text-gray-500">-</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">-</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button className="text-gray-600 hover:text-gray-900 p-1" title="Upravit" onClick={e => handleOpenEditCustomerModal(customer, e)}>
                              <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                            </button>
                            <button className="text-blue-600 hover:text-blue-900 p-1" title="Přidat poznámku" onClick={e => handleOpenAddNoteModal(customer, e)}>
                              <FontAwesomeIcon icon={faStickyNote} className="w-4 h-4" />
                            </button>
                            <button className="text-green-600 hover:text-green-900 p-1" title="Vytvořit rezervaci" onClick={e => handleOpenBookingModal(customer, e)}>
                              <FontAwesomeIcon icon={faCalendar} className="w-4 h-4" />
                            </button>
                            <button className="text-red-600 hover:text-red-900 p-1" title="Smazat" onClick={e => handleOpenDeleteDialog(customer, e)}>
                              <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <section className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Zobrazeno {(currentPage - 1) * 20 + 1} - {Math.min(currentPage * 20, pagination.totalElements)} z {pagination.totalElements} zákazníků
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                  </button>

                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          currentPage === page ? 'bg-green-600 text-white' : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>

      {/* Modal pro vytvoření nové rezervace */}
      {isBookingModalOpen && selectedCustomerForBooking && (
        <CreateBookingModal
          isOpen={isBookingModalOpen}
          onClose={handleCloseBookingModal}
          selectedDate={dayjs().format('YYYY-MM-DD')}
          salonId={entityId || ''}
          staffList={staffList.map(s => ({ id: s.id, name: s.name }))}
          servicesList={servicesList.map(s => ({
            id: s.id,
            name: s.name,
            price: s.price || 0,
            duration: s.duration || 0,
          }))}
          preSelectedCustomer={{
            firstName: selectedCustomerForBooking.firstName,
            lastName: selectedCustomerForBooking.lastName,
            phone: selectedCustomerForBooking.phone,
            email: selectedCustomerForBooking.email,
          }}
          onFinish={() => {
            // Po dokončení rezervace můžeme reload zákazníků nebo zobrazit toast
            handleCloseBookingModal();
          }}
        />
      )}

      {/* Modal pro vytvoření/editaci zákazníka */}
      <CustomerModal isOpen={isCustomerModalOpen} onClose={handleCloseCustomerModal} mode={customerModalMode} customer={editingCustomer} onSave={handleSaveCustomer} />

      {/* Dialog pro potvrzení smazání */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteCustomer}
        title="Smazat zákazníka?"
        message={
          customerToDelete
            ? `Opravdu chcete smazat zákazníka ${customerToDelete.firstName} ${customerToDelete.lastName}? Tato akce je nevratná a smaže všechny údaje včetně historie rezervací.`
            : ''
        }
        confirmText="Ano, smazat"
        cancelText="Zrušit"
        variant="danger"
      />

      {/* Modal pro přidání poznámky */}
      <AddNoteModal
        isOpen={isAddNoteModalOpen}
        onClose={() => {
          setIsAddNoteModalOpen(false);
          setCustomerForNote(null);
        }}
        onSave={handleSaveNote}
        customerName={customerForNote ? `${customerForNote.firstName} ${customerForNote.lastName}` : undefined}
      />
    </div>
  );
};

export default CustomersPage;
